import { useMemo } from 'react';
import {
  cardMultiplier,
  deriveResult,
  scorerIdsFromEvents,
  type Fixture,
  type Prediction
} from '@h2h/shared';
import { TeamBadge } from '../../../components/TeamBadge';
import { BasePickPill } from '../../../components/play/slate-pick/BasePickCornerStamp';
import { Lock, Trophy } from '../../../components/ui/icons';
import { fixtureCardPhase } from '../../../lib/footballRoundUi';
import { formatMultiplier, signedPoints } from '../../../lib/format';
import { teamCode, shortTeam } from '../../../lib/teams';
import { cn } from '../../../lib/cn';
import { timeLabel, useNow } from '../helpers';
import { gradePick, isPerfectCard } from './gradePick';

export function FixtureRow({ fixture, pick, onOpen }: { fixture: Fixture; pick?: Prediction; onOpen: () => void }) {
  const now = useNow();
  const hasCard = !!pick;
  const home = teamCode(fixture.homeTeam, fixture.homeCode);
  const away = teamCode(fixture.awayTeam, fixture.awayCode);
  const isLive = fixture.status === 'LIVE';
  const isSettled = fixture.status === 'SETTLED' && !!fixture.result;
  const isVoid = fixture.status === 'VOID';
  const phase = fixtureCardPhase(fixture, hasCard, now);
  const missed = phase === 'missed';

  const homeGoals = isSettled ? fixture.result!.homeGoals : fixture.liveHomeGoals;
  const awayGoals = isSettled ? fixture.result!.awayGoals : fixture.liveAwayGoals;
  const hasScore = (isSettled || isLive) && homeGoals != null && awayGoals != null;
  const score = hasScore ? `${homeGoals}–${awayGoals}` : null;

  const grade = useMemo(() => {
    if (!pick || isVoid) return null;
    if (isSettled) {
      const g = gradePick(pick, fixture.result!);
      return { value: g.points, live: false, correct: g.correct, attempted: g.attempted, perfect: g.perfect };
    }
    if (isLive && (fixture.liveHomeGoals != null || fixture.liveAwayGoals != null)) {
      const liveHt =
        fixture.liveHalftimeHome != null && fixture.liveHalftimeAway != null
          ? { home: fixture.liveHalftimeHome, away: fixture.liveHalftimeAway }
          : null;
      const liveResult = deriveResult(
        fixture.fixtureId,
        fixture.liveHomeGoals ?? 0,
        fixture.liveAwayGoals ?? 0,
        scorerIdsFromEvents(fixture.events),
        liveHt
      );
      const g = gradePick(pick, liveResult);
      return { value: g.points, live: true, correct: g.correct, attempted: g.attempted, perfect: g.perfect };
    }
    return null;
  }, [pick, fixture, isSettled, isLive, isVoid]);

  const perfect = !!grade && !grade.live && isPerfectCard(grade.correct, grade.attempted);

  // ── Card shell (matches RoundOverview exactly) ─────────────────────────────
  const cardShell = cn(
    'tap w-full overflow-hidden rounded-[20px] text-left transition-[transform,box-shadow] duration-150 ease-ios-out active:scale-[0.99]',
    isLive
      ? 'border border-[rgba(239,65,53,0.2)] shadow-[0_8px_22px_-10px_rgba(239,65,53,0.2)]'
      : perfect
        ? 'border border-[rgba(36,197,106,0.3)] shadow-[0_8px_22px_-8px_rgba(11,160,120,0.3)]'
        : hasCard
          ? 'border border-black/[0.08] shadow-[0_8px_24px_-8px_rgba(20,20,50,0.16),0_2px_6px_-2px_rgba(20,20,50,0.06)]'
          : missed
            ? 'border border-dashed border-black/[0.12] opacity-50'
            : phase === 'open'
              ? 'border border-dashed border-black/[0.18]'
              : 'border border-black/[0.08]'
  );

  const headerBg = isLive ? 'bg-white' : isSettled || isVoid ? 'bg-[#F8F8FA]' : 'bg-white';
  const footerBg = isLive
    ? 'bg-[rgba(239,65,53,0.04)] border-t border-[rgba(239,65,53,0.1)]'
    : isSettled || isVoid
      ? 'bg-[#F2F2F5] border-t border-black/[0.05]'
      : perfect
        ? 'bg-[rgba(36,197,106,0.06)] border-t border-[rgba(36,197,106,0.15)]'
        : 'bg-[#F7F7FB] border-t border-[#EEEEF2]';

  // Time info shown under team names
  const timeInfo = isLive
    ? fixture.elapsed != null ? `${fixture.elapsed}'` : 'Live'
    : isSettled
      ? `${timeLabel(fixture.kickoffAt)} · FT`
      : isVoid
        ? `${timeLabel(fixture.kickoffAt)} · Void`
        : timeLabel(fixture.kickoffAt);

  const ringClass = 'border border-black/[0.08]';

  return (
    <button type="button" onClick={onOpen} className={cardShell}>
      {/* ── Header zone ────────────────────────────────────────────────────── */}
      <div className={cn('px-[14px] py-[13px]', headerBg)}>
        {/* Live / Settled strip */}
        {(isLive || isSettled || isVoid) && (
          <div className={cn(
            'mb-[10px] flex items-center gap-[6px] rounded-[8px] px-2 py-[5px]',
            isLive ? 'bg-[rgba(239,65,53,0.08)]' : 'bg-[#EAEAEE]'
          )}>
            {isLive
              ? <span className="h-[5px] w-[5px] shrink-0 animate-h2h-pulse rounded-full bg-[#EF4135]" />
              : <Lock width={9} height={9} strokeWidth={2.6} className="shrink-0 text-ink3" />}
            <span className={cn(
              'flex-1 text-[10.5px] font-extrabold tracking-[0.02em]',
              isLive ? 'text-[#C0392B]' : 'text-ink3'
            )}>
              {isLive ? (fixture.elapsed != null ? `Live · ${fixture.elapsed}'` : 'Live now') : isVoid ? 'Voided' : 'Full time'}
            </span>
            {score && (
              <span className={cn('num font-extrabold tracking-[-0.02em]', isLive ? 'text-[13px] text-ink' : 'text-[12px] text-ink2')}>
                {score}
              </span>
            )}
          </div>
        )}

        <div className="flex items-center gap-[11px]">
          {/* Overlapping team badges */}
          <div className="flex shrink-0 items-center">
            <TeamBadge code={home} logo={fixture.homeLogo} name={fixture.homeTeam} sizeClass="h-[32px] w-[32px]" roundedClass="rounded-[9px]" textClass="text-[8px]" className={cn(ringClass, (isLive || isSettled) && 'opacity-70')} />
            <TeamBadge code={away} logo={fixture.awayLogo} name={fixture.awayTeam} sizeClass="h-[32px] w-[32px]" roundedClass="rounded-[9px]" textClass="text-[8px]" className={cn('-ml-[9px]', ringClass, (isLive || isSettled) && 'opacity-70')} />
          </div>

          {/* Team names + time */}
          <div className="min-w-0 flex-1">
            <div className={cn('truncate text-[14px] font-extrabold tracking-[-0.015em]', isSettled || isVoid ? 'text-ink2' : 'text-ink')}>
              {shortTeam(fixture.homeTeam)} <span className="font-semibold text-[#CACAD2]">v</span> {shortTeam(fixture.awayTeam)}
            </div>
            <div className={cn('mt-[3px] text-[11.5px] font-semibold', isLive ? 'text-[#C0392B]' : 'text-ink3')}>
              {timeInfo}
            </div>
          </div>

          {/* Right: pick badge or state chip */}
          {hasCard && pick?.outcome ? (
            <BasePickPill
              outcome={pick.outcome}
              fixture={fixture}
              homeCode={home}
              awayCode={away}
              flag={false}
            />
          ) : phase === 'open' ? (
            <span className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-full bg-night">
              <span className="text-[16px] font-bold leading-none text-white">+</span>
            </span>
          ) : missed ? (
            <span className="inline-flex items-center gap-1 rounded-[8px] bg-[#EAEAEE] px-2 py-[5px] text-[10.5px] font-extrabold text-ink3">
              No card
            </span>
          ) : isLive ? (
            <span className="inline-flex items-center gap-[5px] rounded-[8px] bg-[rgba(239,65,53,0.10)] px-2 py-[5px] text-[10.5px] font-extrabold text-[#C0392B]">
              <span className="h-[5px] w-[5px] shrink-0 animate-h2h-pulse rounded-full bg-[#EF4135]" />
              Live
            </span>
          ) : null}
        </div>
      </div>

      {/* ── Footer zone: card stats ─────────────────────────────────────────── */}
      {hasCard && (
        <div className={cn('px-[14px] py-[11px]', footerBg)}>
          <div className="flex items-center justify-between gap-3">
            {/* Left: multiplier chip + status label */}
            <div className="flex min-w-0 items-center gap-[7px]">
              <span className={cn(
                'shrink-0 rounded-[7px] border px-[8px] py-[4px] text-[11px] font-extrabold',
                isSettled || isVoid
                  ? 'border-black/[0.06] bg-[#EAEAEE] text-ink3'
                  : 'border-black/[0.07] bg-white text-ink shadow-[0_1px_2px_rgba(20,20,50,0.08)]'
              )}>
                ×{formatMultiplier(cardMultiplier(pick?.legs ?? []))}
              </span>
              <span className="text-[11px] font-semibold text-ink3">
                {perfect ? null : grade && grade.attempted > 0
                  ? `${grade.correct}/${grade.attempted} legs`
                  : phase === 'saved'
                    ? 'Locks at kickoff'
                    : isVoid
                      ? 'Voided'
                      : 'Locked in'}
              </span>
              {perfect && (
                <span className="relative inline-flex items-center gap-[4px] overflow-hidden rounded-[7px] bg-[linear-gradient(135deg,#2EDB7B,#0BA078)] px-[8px] py-[4px] text-[10.5px] font-extrabold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.3),0_4px_10px_-4px_rgba(11,160,120,0.7)]">
                  <span className="pointer-events-none absolute inset-y-0 w-[42%] animate-h2h-sheen bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.4),transparent)]" />
                  <Trophy width={10} height={10} strokeWidth={2.4} className="relative shrink-0" />
                  <span className="relative">Perfect</span>
                </span>
              )}
            </div>

            {/* Right: points */}
            {isVoid ? (
              <span className="shrink-0 text-[11px] font-semibold text-ink3">Void</span>
            ) : grade ? (
              <div className="shrink-0 text-right">
                <div className={cn('num text-[20px] font-extrabold leading-none tracking-[-0.03em]', grade.value > 0 ? 'text-[#0A9A72]' : grade.value < 0 ? 'text-[#EF4135]' : 'text-ink3')}>
                  {signedPoints(grade.value)}
                </div>
                <div className="mt-[2px] text-[9px] font-bold uppercase tracking-[0.04em] text-ink3">
                  {grade.live ? 'live' : 'pts'}
                </div>
              </div>
            ) : phase === 'saved' ? (
              <span className="inline-flex items-center gap-[5px] rounded-[7px] bg-accent/[0.1] px-[8px] py-[4px] text-[10px] font-extrabold text-accent-ink">
                Editable
              </span>
            ) : (
              <span className="shrink-0 text-[11px] font-semibold text-ink3">Locked in</span>
            )}
          </div>
        </div>
      )}

      {/* No-card footer nudges */}
      {!hasCard && phase === 'open' && (
        <div className="border-t border-dashed border-black/[0.12] bg-[#FAFAFA] px-[14px] py-[10px]">
          <span className="text-[11px] font-semibold text-accent-ink">Tap to build a card</span>
        </div>
      )}
      {!hasCard && missed && (
        <div className="border-t border-black/[0.06] bg-[#F8F8FA] px-[14px] py-[10px]">
          <span className="text-[11px] font-semibold text-ink3">No card built · no points</span>
        </div>
      )}
    </button>
  );
}
